import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { useLazyGlobalSearchQuery } from "@/pages/Private/redux/search/search.api";

import { useLazyGetCompanyEnrichPersonQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";
import { classNames } from "@/utils/classNames";
import { Avatar } from "@/components/Avatar/Avatar";
import { CompanyLogoAvatar } from "@/components/CompanyLogoAvatar/CompanyLogoAvatar";
import { Spinner } from "@/components/Spinner/Spinner";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { parseLinkedinProfileUrl } from "../utils/linkedinProfileUrl";
import { personDisplayCompany } from "../utils/personDisplayCompany";

import type { IPersonSummary } from "@/pages/Private/redux/search/search.schema";
import type { CompanyEnrichPersonDetail } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

export interface LinkedinProfileSelection {
	name: string;
	position?: string | null;
	imageUrl?: string | null;
	linkedinUrl: string;
	/** Upstream id of the person's current company — used to suggest the company's LinkedIn page. */
	companyEnrichCompanyId?: string | null;
	/** Employer shown under the name, so the confirmed chip says what the picked row said. */
	companyName?: string | null;
	companyLogoUrl?: string | null;
}

/** Splits a display name for the shared Avatar's initials. */
const nameParts = (name: string): { firstName: string; lastName: string } => {
	const parts = name.trim().split(" ");

	return { firstName: parts[0] ?? "", lastName: parts[parts.length - 1] ?? "" };
};

/** The person's current (or matched) experience carries the company we suggest. */
const currentCompanyIdOf = (detail: CompanyEnrichPersonDetail): string | null => {
	const experiences = detail.experiences ?? [];
	const current =
		experiences.find((experience) => experience.isCurrent) ??
		experiences.find((experience) => experience.isMatched) ??
		experiences[0];

	return current?.company ? String(current.company.id) : null;
};

interface ProfileSummaryProps {
	name: string;
	position?: string | null;
	companyName?: string | null;
	companyLogoUrl?: string | null;
}

/**
 * The three lines a person is identified by in this typeahead: name, job title
 * and employer. The employer is what tells two namesakes with the same job title
 * apart, so it renders whenever we could resolve one (PREC-3903).
 */
const ProfileSummary: FunctionComponent<ProfileSummaryProps> = ({
	name,
	position,
	companyName,
	companyLogoUrl,
}) => (
	<span className="min-w-0 flex-1">
		<span className="block truncate text-xs font-semibold leading-tight">{name}</span>
		{position && (
			<span className="block truncate text-[11px] leading-tight text-text-meta">{position}</span>
		)}
		{companyName && (
			<span className="flex items-center gap-1 text-[11px] leading-tight text-text-meta">
				<CompanyLogoAvatar
					className="rounded-sm"
					logoUrl={companyLogoUrl ?? null}
					name={companyName}
					size="xs"
				/>
				<span className="truncate">{companyName}</span>
			</span>
		)}
	</span>
);

interface LinkedinProfileComboboxProps {
	value: LinkedinProfileSelection | null;
	onSelect: (selection: LinkedinProfileSelection) => void;
	onClear: () => void;
	/** Fired when a picked person turns out to have no LinkedIn URL — switch to manual URL entry. */
	onManualEntry: () => void;
	portalContainer?: HTMLElement | null;
}

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_RESULTS = 5;

/**
 * Typeahead for the user's personal LinkedIn profile (Profile & Company
 * onboarding step): searches people by name via global search, resolves the
 * picked person's LinkedIn URL from the person detail endpoint.
 */
export const LinkedinProfileCombobox: FunctionComponent<LinkedinProfileComboboxProps> = ({
	value,
	onSelect,
	onClear,
	onManualEntry,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.profileStep.${key}`);

	const [query, setQuery] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [results, setResults] = useState<IPersonSummary[]>([]);
	const [resolvingId, setResolvingId] = useState<number | null>(null);
	const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
		null
	);

	const anchorRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, isOpen);

	const [fetchSearch, { isFetching: isSearching }] = useLazyGlobalSearchQuery();
	const [fetchPersonDetail] = useLazyGetCompanyEnrichPersonQuery();

	/** A typed profile URL is used as-is — the same field takes a name or a URL. */
	const typedUrl = parseLinkedinProfileUrl(query);

	useEffect(() => {
		// A URL is not a search query: offer it directly instead of searching for it.
		if (parseLinkedinProfileUrl(query)) {
			setResults([]);
			setIsOpen(true);

			return;
		}

		if (query.trim().length < MIN_QUERY_LENGTH) {
			setResults([]);
			setIsOpen(false);

			return;
		}

		const handle = setTimeout(async () => {
			try {
				const response = await fetchSearch({
					personSearchSource: { query: query.trim() },
				}).unwrap();

				setResults(response.companyEnrich?.items?.slice(0, MAX_RESULTS) ?? []);
				setIsOpen(true);
			} catch (err) {
				console.error(err);
			}
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(handle);
	}, [query, fetchSearch]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const updateRect = () => {
			const rect = anchorRef.current?.getBoundingClientRect();

			if (rect) {
				setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
			}
		};

		updateRect();
		window.addEventListener("scroll", updateRect, true);
		window.addEventListener("resize", updateRect);

		return () => {
			window.removeEventListener("scroll", updateRect, true);
			window.removeEventListener("resize", updateRect);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleOutsideClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (
				target instanceof Node &&
				!anchorRef.current?.contains(target) &&
				!menuRef.current?.contains(target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);

		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isOpen]);

	const handleUseTypedUrl = () => {
		if (!typedUrl) {
			return;
		}

		onSelect({ name: typedUrl.name, linkedinUrl: typedUrl.url });
		setIsOpen(false);
		setQuery("");
	};

	const handlePick = async (person: IPersonSummary) => {
		setResolvingId(person.id);

		const company = personDisplayCompany(person);

		try {
			const detail = await fetchPersonDetail(person.id).unwrap();
			const linkedinUrl = detail.socials?.linkedin_url;

			if (!linkedinUrl) {
				onManualEntry();

				return;
			}

			onSelect({
				name: person.name,
				position: person.position,
				imageUrl: person.image_url,
				linkedinUrl,
				companyEnrichCompanyId: currentCompanyIdOf(detail),
				companyName: company?.name,
				companyLogoUrl: company?.logoUrl,
			});
			setIsOpen(false);
			setQuery("");
		} catch (err) {
			console.error(err);
			onManualEntry();
		} finally {
			setResolvingId(null);
		}
	};

	if (value) {
		// `.input-styles` pins a 48px height for a single-line field, which the chip's name,
		// job title and employer overflow — it takes the styling but sizes itself to its content.
		return (
			<div className="input-styles flex h-auto min-h-[48px] w-full items-center gap-2 rounded-lg border px-3 py-2">
				<Avatar
					firstName={nameParts(value.name).firstName}
					imageSrc={value.imageUrl ?? undefined}
					lastName={nameParts(value.name).lastName}
				/>
				<ProfileSummary
					companyLogoUrl={value.companyLogoUrl}
					companyName={value.companyName}
					name={value.name}
					position={value.position}
				/>
				<button
					className="cursor-pointer p-1 text-text-meta hover:text-status-error"
					title={ts("clearSelection")}
					type="button"
					onClick={onClear}
				>
					✕
				</button>
			</div>
		);
	}

	const typedUrlMenu = typedUrl ? (
		<button
			className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
			type="button"
			onClick={handleUseTypedUrl}
		>
			<Avatar
				firstName={nameParts(typedUrl.name).firstName}
				lastName={nameParts(typedUrl.name).lastName}
			/>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-xs font-semibold">{typedUrl.name}</span>
				<span className="block truncate text-[11px] text-text-meta">{ts("useTypedUrl")}</span>
			</span>
		</button>
	) : null;

	const menu =
		isOpen && menuRect ? (
			<div
				ref={menuRef}
				className="fixed z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
				style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
			>
				{typedUrlMenu ??
					(isSearching && results.length === 0 ? (
						<div className="flex items-center justify-center p-3">
							<Spinner color="#4D66F5" />
						</div>
					) : (
						results.map((person) => {
							const company = personDisplayCompany(person);

							return (
								<button
									key={person.id}
									className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50"
									type="button"
									onClick={() => handlePick(person)}
								>
									<Avatar
										firstName={nameParts(person.name).firstName}
										imageSrc={person.image_url ?? undefined}
										lastName={nameParts(person.name).lastName}
									/>
									<ProfileSummary
										companyLogoUrl={company?.logoUrl}
										companyName={company?.name}
										name={person.name}
										position={person.position}
									/>
									{resolvingId === person.id && <Spinner color="#4D66F5" />}
								</button>
							);
						})
					))}
				{!typedUrl && (
					<div className="border-t border-gray-200 px-3 py-2 text-[11px] text-text-meta">
						{ts("pasteProfileUrlHint")}
					</div>
				)}
			</div>
		) : null;

	return (
		<div ref={anchorRef} className="w-full">
			<input
				autoComplete="off"
				className={classNames("input-styles w-full rounded-lg border px-3 py-2 text-xs")}
				placeholder={ts("typeaheadPlaceholder")}
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				onFocus={() => {
					if (typedUrl || (results.length > 0 && query.trim().length >= MIN_QUERY_LENGTH)) {
						setIsOpen(true);
					}
				}}
			/>
			{menu && ReactDOM.createPortal(menu, portalContainer ?? document.body)}
		</div>
	);
};
